import type { Difficulty, Grade } from "../types";

export const DIFFICULTIES: Difficulty[] = ["einfach", "medium", "schwer", "sehr-schwer"];
export const DIFFICULTY_ORDER: Record<Difficulty, number> = {
  einfach: 1,
  medium: 2,
  schwer: 3,
  "sehr-schwer": 4,
};
export const DEFAULT_ABILITY = 1.8;

export function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

// Deterministischer Hash (FNV-1a) -> Seed, damit Mischung/Stufenwahl reproduzierbar sind.
export function hashSeed(...parts: Array<string | number>): number {
  const s = parts.join("|");
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// mulberry32: kleiner, schneller, deterministischer PRNG.
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function seededShuffle<T>(items: T[], seed: number): T[] {
  const arr = [...items];
  const rand = rng(seed);
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// Assembliert die anzuzeigenden Optionen (richtig + Distraktoren der Stufe) deterministisch
// und liefert die Antwort-Indizes in der gemischten Reihenfolge.
export function buildOptions(
  correct: string[],
  distractors: string[],
  seed: number
): { options: string[]; answers: number[] } {
  const tagged = [
    ...correct.map((t) => ({ t, c: true })),
    ...distractors.map((t) => ({ t, c: false })),
  ];
  const shuffled = seededShuffle(tagged, seed);
  const options = shuffled.map((o) => o.t);
  const answers = shuffled.map((o, i) => (o.c ? i : -1)).filter((i) => i >= 0);
  return { options, answers };
}

export function abilityFor(abilities: Record<string, number>, skillTags: string[]): number {
  const vals = skillTags.map((s) => abilities[s]).filter((v): v is number => typeof v === "number");
  if (!vals.length) return DEFAULT_ABILITY;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

// Zieht die Distraktor-Stufe aus dem Niveau: Mix statt monoton (~60% auf Niveau,
// ~25% eine Stufe leichter zum Festigen, ~15% eine Stufe schwerer als Stretch).
export function pickDifficulty(ability: number, seed: number): Difficulty {
  const base = Math.round(clamp(ability, 1, 4));
  const r = rng(seed)();
  let level = base;
  if (r >= 0.85) level = base + 1;
  else if (r >= 0.6) level = base - 1;
  return DIFFICULTIES[clamp(level, 1, 4) - 1];
}

export function gradeToScore(grade: Grade): number {
  return grade === "again" ? 0 : grade === "hard" ? 0.4 : grade === "easy" ? 1 : 0.85;
}

// Elo-artiges Update: erwartete Leistung logistisch auf (ability - itemLevel); K moderat.
// Ein Treffer auf hoher Stufe hebt staerker, ein Fehler auf niedriger Stufe senkt staerker.
export function updateAbility(prev: number, itemLevel: number, score: number): number {
  const expected = 1 / (1 + Math.pow(10, (itemLevel - prev) / 1.2));
  const K = 0.35;
  return clamp(prev + K * (score - expected), 1, 4);
}
